import classNames from 'classnames'
import moment from 'moment'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import {
    ImgButton,
    SimpleBlueButton
} from '../../components/core/button'
import CustomCalendar from '../../components/core/calendar'
import CustomSelect from '../../components/core/customSelect'
import Input from '../../components/core/input'
import InputWithIcon from '../../components/core/inputWithIcon'
import ContentLoader from '../../components/core/loader'
import Table from '../../components/core/table'
import {
    PageSubTitle,
    PageTitle
} from '../../components/core/title'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import Tooltip from '../../components/core/tooltip'
import Footer from '../../components/layouts/footer'
import TeamSwitcher from '../../components/pages/teams'
import cronJobRulesetTypes from '../../data/cronJobRulesetTypes.json'
import {
    EventIcon,
    HelpIcon,
    LoadMoreIcon,
    SearchIcon
} from '../../icons'
import {routes} from '../../routes'
import {getJob, getJobEvents, getJobs} from '../../stores/heartbeats'
import {getServices} from '../../stores/resources'
import {setSelectedTeamId} from '../../stores/teams'
import {colors} from '../../utils/colors'
import DateHelper, {formatToISOUTC, modifyUTCDate} from '../../utils/date'
import renderSkeletonLoaders from '../../utils/loader'
import EmptyContent from '../resources/EmptyContent'
import './index.scss'
import text from './text.json'
import {sortBySelected} from '../../utils/sorting'
import localStorageManager from '../../utils/localStorageManager'
import localStorageKeys from '../../utils/localStorageKeys'

const SORT_ID = 'job-events'
const EVENTS_COUNT = 10
const URL_PARAMS = {
    job_id: 'id',
    last_received_at: 'last_received_at',
    team_id: 'team_id'
}

const formatStartingFrom = (date, time) => {
    const formattedTime = time?.format?.('HH:mm:ss [UTC]') || '00:00:00 UTC'
    return `${date}, ${formattedTime}`
}

const JobEvents = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)

    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const {services, isFetching: isServicesFetching} = useSelector(state => state.resources)
    const selectedTeamId = useSelector(state => state.teams.selectedTeamId)

    const [jobEvents, setJobEvents] = useState({})
    const [jobs, setJobs] = useState([])
    const [filteredJobs, setFilteredJobs] = useState([])
    const [isJobsFetching, setIsJobsFetching] = useState(false)
    const [isEventsFetching, setIsEventsFetching] = useState(true)
    const [isFilterEnabled, setIsFilterEnabled] = useState(false)
    const [isLoadMoreEnabled, setIsLoadMoreEnabled] = useState(true)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [isButtonLoading, setIsButtonLoading] = useState(false)
    const [startTime, setStartTime] = useState(moment())
    const isTableSorted = localStorageManager.get(localStorageKeys.SORT_OPTIONS)?.[SORT_ID]

    const currentDate = new Date()

    const initialSettings = useMemo(() => ({
        team_id: selectedTeamId,
        selectedJobs: [],
        show_until: null,
        ip_or_message: ''
    }), [selectedTeamId])

    const [filterSettings, setFilterSettings] = useState(initialSettings)

    const jobEventsValues = useMemo(() => Object.values(jobEvents).flatMap(ev => ev?.data || []), [jobEvents])

    const fetchJobs = useCallback(async(searchedId, lastReceivedAt) => {
        setIsJobsFetching(true)
        await dispatch(getServices(true))

        const jobList = await dispatch(getJobs())
        const formattedJobs = jobList?.map(job => ({value: job?.heartbeat_id, label: job?.name})) || []
        setJobs(formattedJobs)

        let selectedJobs = formattedJobs
        if (searchedId) {
            const job = await dispatch(getJob(searchedId, navigate))
            selectedJobs = job ? [{value: job.id, label: job.name}] : []
        }

        const updatedFilter = {
            ...filterSettings,
            selectedJobs,
            show_until: lastReceivedAt && DateHelper.convertDateToUTC(lastReceivedAt)
        }
        setFilterSettings(updatedFilter)

        if (searchedId || isFirstLoad) {
            await fetchJobEvents(updatedFilter, false, lastReceivedAt)
        }
        setIsJobsFetching(false)
    }, [dispatch, isFirstLoad, filterSettings])

    const fetchJobEvents = useCallback(async(settings, loadMore = false, customLastReceivedAt = '') => {
        setIsFirstLoad(false)

        if (!settings.selectedJobs?.length) {
            setJobEvents({})
            setIsEventsFetching(false)
            return
        }

        const updatedEvents = loadMore ? {...jobEvents} : {}

        await Promise.all(settings.selectedJobs.map(async({value}) => {
            let lastReceivedAt = customLastReceivedAt

            if (loadMore && updatedEvents[value]?.data?.length) {
                const latest = updatedEvents[value].data.reduce((a, b) =>
                    new Date(a.received_at) < new Date(b.received_at) ? a : b
                )
                lastReceivedAt = latest?.received_at || ''
            }

            const {data = [], count = null} = await dispatch(getJobEvents(value, navigate, lastReceivedAt, EVENTS_COUNT))

            const filteredData = data.filter(item =>
                item.ip.includes(settings.ip_or_message.trim()) ||
                item.message.includes(settings.ip_or_message.trim())
            )

            updatedEvents[value] = {
                data: [...(updatedEvents[value]?.data || []), ...filteredData],
                count
            }
        }))

        setJobEvents(updatedEvents)
        setIsLoadMoreEnabled(!Object.values(updatedEvents).every(ev => (ev?.count || 0) < EVENTS_COUNT))
        setIsEventsFetching(false)
        if (loadMore) setIsButtonLoading(false)
    }, [dispatch, navigate, jobEvents])

    const applyFilters = async() => {
        clearUrlParams()
        setIsFilterEnabled(true)

        const dateOnly = filterSettings.show_until?.split(',')[0]
        const fullDate = modifyUTCDate(DateHelper.convertSlashDateToJSDate(dateOnly, 'DD/MM/YYYY'), startTime)
        const formattedDate = formatToISOUTC(fullDate)

        await fetchJobEvents(filterSettings, false, formattedDate)
    }

    const resetFilters = async() => {
        clearUrlParams()
        const resetSettings = {...initialSettings, selectedJobs: jobs}
        setFilterSettings(resetSettings)
        await fetchJobEvents(resetSettings)
        setIsFilterEnabled(false)
    }

    const clearUrlParams = () => {
        [URL_PARAMS.job_id, URL_PARAMS.last_received_at, URL_PARAMS.team_id].map(param => urlParams.delete(param))
        navigate(routes.job_events.path)
    }

    const getSearchedJob = useCallback(async() => {
        const searchedId = urlParams.get(URL_PARAMS.job_id)
        const teamId = urlParams.get(URL_PARAMS.team_id)
        if (teamId) dispatch(setSelectedTeamId(teamId))
        if (searchedId) {
            const lastReceived = urlParams.get(URL_PARAMS.last_received_at)
            await fetchJobs(searchedId, lastReceived)
        }
    }, [dispatch, navigate, urlParams])

    const handleDateChange = (date) => {
        const newValue = formatStartingFrom(date, startTime)
        setFilterSettings(prev => ({...prev, show_until: newValue}))
    }

    const handleStartTimeChange = (time) => {
        setStartTime(time)
        const datePart = filterSettings?.show_until?.split(',')[0] || DateHelper.convertToDateOnly(currentDate, 'DD.MM.YYYY')
        const newValue = formatStartingFrom(datePart, time)
        setFilterSettings(prev => ({...prev, show_until: newValue}))
    }

    const getSortedData = useCallback(() => {
        const data = [...jobEventsValues]
        return isTableSorted
            ? sortBySelected(data, SORT_ID, tableHeader)
            : data.sort((a, b) => new Date(b.received_at) - new Date(a.received_at))
    }, [jobEventsValues, isTableSorted, SORT_ID])

    useEffect(() => {
        setFilterSettings(prev => ({...prev, team_id: selectedTeamId}))
        if (isFirstLoad && urlParams.size) {
            getSearchedJob()
            return
        }
        fetchJobs()
    }, [dispatch, selectedTeamId, urlParams.size])

    useEffect(() => {
        setFilteredJobs(getSortedData())
    }, [jobEventsValues, isTableSorted])

    const getEventResultType = type => {
        const {condition: {options}} = cronJobRulesetTypes.result
        const result = options[type] || options.default
        return <span className={`result-container ${result.value}`}>{result.label}</span>
    }

    const tableHeader = useMemo(() => [
        {
            label: 'Event time',
            className: 'min-width-300',
            sorting: {
                asc: {key: 'received_at_asc', field: ['received_at']},
                desc: {key: 'received_at_desc', field: ['received_at']}
            }
        },
        {
            label: 'IP',
            className: 'min-width-120',
            sorting: {
                asc: {key: 'ip_asc', field: ['ip']},
                desc: {key: 'ip_desc', field: ['ip']}
            }
        },
        {
            label: 'Result',
            className: 'min-width',
            sorting: {
                asc: {key: 'result_asc', field: ['result']},
                desc: {key: 'result_desc', field: ['result']}
            }
        },
        {
            label: 'Message',
            className: 'min-width',
            sorting: {
                asc: {key: 'message_asc', field: ['message']},
                desc: {key: 'message_desc', field: ['message']}
            }
        }
    ], [])

    const tableBody = useMemo(() => filteredJobs.map(event => [
        <span className='min-width-300 gap-1 event-time'>
            <EventIcon />
            <span>{DateHelper.convertDateToUTC(event.received_at)}</span>
        </span>,
        <span className='min-width-120'>{event.ip}</span>,
        <span className={classNames('min-width body-cell', {
            dark: isDarkMode
        })}>
            {getEventResultType(event.result)}
        </span>,
        <span className='min-width'>{event.message || text.not_available}</span>
    ]), [filteredJobs, isDarkMode])

    const renderEventsTable = () => {
        if (isEventsFetching) return <ContentLoader />
        if (!filteredJobs.length) return <EmptyContent message={text.no_data} hideButton />

        return (
            <div className="job-events-container">
                <Table
                    tableHeader={tableHeader}
                    data={jobEventsValues}
                    tbodyData={tableBody}
                    identificator="id"
                    hasCheckbox={false}
                    isSortingEnabled
                    setData={setFilteredJobs}
                    callbackFn={() => setFilteredJobs(getSortedData())}
                    sortId={SORT_ID}
                    filteredData={filteredJobs}
                />
                {isLoadMoreEnabled && (
                    <ImgButton
                        onClick={() => {
                            setIsButtonLoading(true)
                            fetchJobEvents(filterSettings, true)
                        }}
                        type="button"
                        className={`load-button ${isButtonLoading ? 'active' : ''}`}
                    >
                        <LoadMoreIcon stroke={colors.colorMuted} />
                        {text.load_more}
                    </ImgButton>
                )}
            </div>
        )
    }

    return (
        <main className={classNames('main job-events', {
            dark: isDarkMode
        })}>
            <div className="content-container">
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                <div className="job-event-filters">
                    <div className="job-event-filters-top">
                        <TeamSwitcher containerClassName="teams" />
                        {isServicesFetching ? renderSkeletonLoaders(1) : (
                            <Input
                                name="service"
                                value={services?.[0]?.label || ''}
                                placeholder={text.service}
                                inputIsDisabled
                                infoText={text.service_info} />
                        )}
                        {isJobsFetching ? renderSkeletonLoaders(1) : (
                            <CustomSelect
                                isMulti
                                containerClassName="jobs"
                                showLabel
                                showSelectedOptions
                                name="jobs"
                                placeholder={text.jobs}
                                options={jobs}
                                value={filterSettings.selectedJobs}
                                onChange={(_, values) => setFilterSettings(prev => ({...prev, selectedJobs: values}))}
                                infoText={text.jobs_info} />
                        )}
                    </div>

                    <div className="job-event-filters-bottom">
                        <CustomCalendar
                            placeholder={text.show_until}
                            onChange={(date) => handleDateChange(date)}
                            value={filterSettings.show_until}
                            currentDate={currentDate}
                            infoText={text.show_until_info}
                            infoIconSize={22}
                            hasTimePicker
                            startTime={startTime}
                            onChangeStartTime={(time) => handleStartTimeChange(time)}
                        />
                        <div className={classNames('ip-or-message-input', {dark: isDarkMode})}>
                            <InputWithIcon
                                placeholder={text.ip_or_message}
                                icon={<SearchIcon />}
                                onChange={e => setFilterSettings(prev => ({...prev, ip_or_message: e.target.value}))}
                                value={filterSettings.ip_or_message}
                                type="text"
                            />
                            <div className="tooltip-container">
                                <Tooltip placement="left" trigger="click" content={text.ip_or_message_info}>
                                    <HelpIcon stroke={colors.colorBgFilter} width="22" height="22" />
                                </Tooltip>
                            </div>
                        </div>
                        <div className="filter-buttons">
                            <SimpleBlueButton
                                className="filter-button"
                                onClick={applyFilters}>
                                {text.filter}
                            </SimpleBlueButton>
                            <SimpleBlueButton
                                className="reset-button"
                                onClick={resetFilters}
                                disabled={!isFilterEnabled && urlParams.size === 0}>
                                {text.reset}
                            </SimpleBlueButton>
                        </div>
                    </div>
                </div>
                {renderEventsTable()}
            </div>
            <Footer />
        </main>
    )
}

export default JobEvents
